import { AllocatorPageBulkAllocationsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { currencyFormatter } from '@/shared/utils/currency';
import { themeVar } from '@/styles/theme';
import { PROPOSAL_TYPE } from '@/types/investments';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { ALLOCATOR_PAGE_UPDATE_ALLOCATIONS_MUTATION } from '../queries';
import { AllocatorTabsEnum, ExtendedBulkAllocation, GroupedLegalEntityCommittedCapital } from '../types';

interface EditAmountProps {
  isOpen: boolean;
  row: ExtendedBulkAllocation | null;
  changedGroupedLegalEntitiesCommittedCapital: Record<string, GroupedLegalEntityCommittedCapital>;
  updateUngroupedEntityAmountInState: ({ entityId, committedCapital }: { entityId: string; committedCapital: number }) => void;
  onChangeGroupedEntityAndAmountInState: ({
    groupId,
    groupedEntityId,
    committedCapital
  }: {
    groupId: string;
    groupedEntityId: string;
    committedCapital: number;
  }) => void;
  bulkAllocationsRefetch: () => Promise<ApolloQueryResult<AllocatorPageBulkAllocationsQuery>>;
  onClose: () => void;
}

const EditAmount = ({
  isOpen,
  row,
  changedGroupedLegalEntitiesCommittedCapital,
  updateUngroupedEntityAmountInState,
  onChangeGroupedEntityAndAmountInState,
  bulkAllocationsRefetch,
  onClose
}: EditAmountProps) => {
  const { searchParams } = useSearchParamsManager();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { handleSubmit, setValue, watch, control } = useForm<{ amount: string; legalEntityId: string }>({
    defaultValues: { amount: '', legalEntityId: '' }
  });

  // Watch the legalEntityId field to use in validation
  const selectedLegalEntityId = watch('legalEntityId');

  const activeTab = (searchParams.get('activeTab') || AllocatorTabsEnum.BeginAllocation) as AllocatorTabsEnum;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupedEntitiesSelectOptions, setGroupedEntitiesSelectOptions] = useState<ISelectValue[]>([]);

  const [updateAllocations] = useMutation(ALLOCATOR_PAGE_UPDATE_ALLOCATIONS_MUTATION);

  const getGroupedLegalEntityValues = (row: ExtendedBulkAllocation, legalEntityId: string) => {
    const groupedLegalEntity = row?.group?.legalEntities?.find((groupedLegalEntity) => groupedLegalEntity.id === legalEntityId);
    return {
      proposalType: groupedLegalEntity?.proposalType,
      committedCapital: groupedLegalEntity?.committedCapital
    };
  };

  const handleEntityAmount = (row: ExtendedBulkAllocation, selectedGroupedLegalEntityId?: string) => {
    const { groupId } = row?.legalEntity || {};
    let committedCapital = row?.committedCapital;

    if (!!groupId) {
      if (changedGroupedLegalEntitiesCommittedCapital?.[groupId]?.groupedEntityId === selectedGroupedLegalEntityId) {
        return changedGroupedLegalEntitiesCommittedCapital[groupId]?.committedCapital?.toString();
      }
      // grouped entity is selected use values from the grouped entity
      const { committedCapital: groupedLegalEntityCommittedCapital } = getGroupedLegalEntityValues(row, selectedGroupedLegalEntityId);
      committedCapital = groupedLegalEntityCommittedCapital;
    }

    return committedCapital?.toString() || '';
  };

  useEffect(() => {
    if (!row?.legalEntity?.groupId) {
      setValue('amount', handleEntityAmount(row) ?? '');
      return;
    }

    const selectedGroupedLegalEntityId = row?.selectedGroupedLegalEntity?.id || '';
    setValue('amount', handleEntityAmount(row, selectedGroupedLegalEntityId) ?? '');
    setValue('legalEntityId', selectedGroupedLegalEntityId);
    setGroupedEntitiesSelectOptions(row?.group?.legalEntities?.map((item) => createSimpleSelectOption(item.id, item.entityName)) || []);
  }, [row]);

  const saveValue = async (data: { amount: string; legalEntityId: string }) => {
    const { amount, legalEntityId } = data;

    if (activeTab === AllocatorTabsEnum.BeginAllocation) {
      if (!!row?.legalEntity?.groupId) {
        onChangeGroupedEntityAndAmountInState({
          groupId: row?.legalEntity?.groupId,
          groupedEntityId: legalEntityId,
          committedCapital: parseFloat(amount)
        });
      } else {
        updateUngroupedEntityAmountInState({
          entityId: row.legalEntity.id,
          committedCapital: parseFloat(amount)
        });
      }
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);

      await updateAllocations({
        variables: {
          data: {
            id: row.id,
            committedCapital: parseFloat(amount),
            ...(!!row?.legalEntity?.groupId && legalEntityId && { legalEntityId })
          }
        }
      });

      await bulkAllocationsRefetch();

      if (!!row?.legalEntity?.groupId) {
        onChangeGroupedEntityAndAmountInState({
          groupId: row?.legalEntity?.groupId,
          groupedEntityId: legalEntityId,
          committedCapital: parseFloat(amount)
        });
      } else {
        updateUngroupedEntityAmountInState({
          entityId: row.legalEntity.id,
          committedCapital: parseFloat(amount)
        });
      }
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!row) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Investment Amount"
      confirmButton={{
        name: 'Save',
        onClick: handleSubmit(saveValue),
        disabled: isSubmitting
      }}
      showRequiredFields={!isSubmitting}
      showCancelButton={!isSubmitting}
      showCloseIcon={!isSubmitting}
      isUseOutsideClick={!isSubmitting}
    >
      {isSubmitting ? (
        <ModalLoader />
      ) : (
        <FieldsWrapper>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          {!!row?.legalEntity?.groupId && (
            <Controller
              name="legalEntityId"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Investor Entity is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  value={value}
                  options={groupedEntitiesSelectOptions}
                  setValue={(entityId) => {
                    onChange(entityId);
                    const amountValue = handleEntityAmount(row, entityId);
                    setValue('amount', amountValue);
                  }}
                  label="Investor Entity"
                  errorText={error?.message}
                  size="lg"
                  tooltip="Select the entity to use for this subscription. This is the entity that will appear on the subscription documents."
                  tooltipWidth={400}
                  isRequiredField
                />
              )}
            />
          )}
          {((!!row?.legalEntity?.groupId && !!watch('legalEntityId')) || !row?.legalEntity?.groupId) && (
            <Controller
              name="amount"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Amount is required'
                },
                min: {
                  value: 1,
                  message: 'Amount must be greater than 0'
                },
                validate: (value) => {
                  const amount = parseFloat(value);
                  let proposalType = row?.proposalType;

                  if (!!row?.legalEntity?.groupId) {
                    const { proposalType: groupedLegalEntityProposalType } = getGroupedLegalEntityValues(row, selectedLegalEntityId);
                    proposalType = groupedLegalEntityProposalType ?? row?.proposalType;
                  }

                  if (proposalType === PROPOSAL_TYPE.INITIAL && amount < row?.investment?.minimum) {
                    return 'Cannot be less than fund minimum';
                  }
                  if (proposalType === PROPOSAL_TYPE.INCREASE && amount <= 0) {
                    return 'Cannot be less than 0';
                  }
                  return true;
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Input
                    value={value ? formatMaskedDollarInputValue(value) : ''}
                    onChange={(e) => {
                      const formattedValue = formatDollarInputValue(e.target.value);
                      onChange(formattedValue);
                    }}
                    label={(() => {
                      if (!row?.legalEntity?.groupId) {
                        return row?.proposalType === PROPOSAL_TYPE.INCREASE ? 'Follow-on Investment Amount' : 'Initial Investment Amount';
                      }

                      const { proposalType: groupedLegalEntityProposalType } = getGroupedLegalEntityValues(row, selectedLegalEntityId);
                      return groupedLegalEntityProposalType === PROPOSAL_TYPE.INCREASE ? 'Follow-on Investment Amount' : 'Initial Investment Amount';
                    })()}
                    errorText={error?.message}
                    styleType={value ? 'success' : 'default'}
                    isRequiredField
                    withDollarSign
                    autoFocus
                    description={(() => {
                      if (!row?.legalEntity?.groupId) {
                        return row?.proposalType === PROPOSAL_TYPE.INCREASE ? '' : `Initial Investment minimum: $${row?.investment?.minimum}`;
                      }

                      const { proposalType: groupedLegalEntityProposalType } = getGroupedLegalEntityValues(row, selectedLegalEntityId);
                      return groupedLegalEntityProposalType === PROPOSAL_TYPE.INCREASE
                        ? ''
                        : `Initial Investment minimum: ${currencyFormatter.format(row?.investment?.minimum || 0)}`;
                    })()}
                  />
                </>
              )}
            />
          )}
        </FieldsWrapper>
      )}
    </ModalWrappedContent>
  );
};

export default EditAmount;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
